import React from "react";
import "./NavigationBar.css";
import { useAuth } from './AuthContext';
import { canAccessUserManagement, canAccessAnalytics, canAccessActivity, ROLES } from './utils/roleUtils';

// Icon components
const AnalyticsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
  </svg>
);

const ActivityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="m22 21-3-3m0 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0"/>
  </svg>
);

const DialerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.2.05-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
  </svg>
);

export default function NavigationBar({ active, onChange }) {
  const { userProfile } = useAuth();
  if (!userProfile) return null;
  
  const userRole = userProfile.role;
  
  // Build tabs based on role permissions
  const tabs = [];
  
  // Admin gets full access
  if (userRole === ROLES.ADMIN) {
    tabs.push(
      { key: 'analytics', label: 'ANALYTICS', icon: <AnalyticsIcon /> },
      { key: 'activity', label: 'ACTIVITY', icon: <ActivityIcon /> },
      { key: 'user-management', label: 'USERS', icon: <UsersIcon /> }
    );
  }
  // Team Lead gets limited access (no user management)
  else if (userRole === ROLES.TEAM_LEAD) {
    tabs.push(
      { key: 'analytics', label: 'ANALYTICS', icon: <AnalyticsIcon /> },
      { key: 'activity', label: 'ACTIVITY', icon: <ActivityIcon /> }
    );
  }
  // Agent gets standard access
  else if (userRole === ROLES.AGENT) {
    tabs.push(
      { key: 'dialer', label: 'DIALER', icon: <DialerIcon /> },
      { key: 'analytics', label: 'ANALYTICS', icon: <AnalyticsIcon /> },
      { key: 'activity', label: 'ACTIVITY', icon: <ActivityIcon /> },
      { key: 'settings', label: 'SETTINGS', icon: <SettingsIcon /> }
    );
  }

  const handleNav = (key) => {
    onChange(key);
  };

  return (
    <nav className="navigation-bar">
      <div className="nav-container">
        {tabs.map((item) => (
          <button
            key={item.key}
            className={`nav-item${active === item.key ? " active" : ""}`}
            onClick={() => handleNav(item.key)}
            aria-label={item.label}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
} 